import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  Copy,
  FileText,
  KeyRound,
  Loader2,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Send,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, EmptyState, StatusPill, fmtDateTime } from "./_shared";

export default function AdminApiKeys() {
  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null); // {plaintext_token, plaintext_webhook_secret, key}
  const [editing, setEditing] = useState(null); // row being edited inline

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [keysRes, scopesRes] = await Promise.all([
        api.get("/admin/api-keys"),
        api.get("/admin/api-keys/scopes"),
      ]);
      setKeys(keysRes.data.keys || []);
      setScopes(scopesRes.data.scopes || []);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load API keys.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onCreate = async (payload) => {
    try {
      const { data } = await api.post("/admin/api-keys", payload);
      toast.success("API key created");
      setCreating(false);
      setCreatedKey(data);
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to create key.",
      );
    }
  };

  const onRevoke = async (key) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Revoke key "${key.name}"? This is irreversible.`)) return;
    try {
      await api.delete(`/admin/api-keys/${key.key_id}`);
      toast.success("Key revoked");
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to revoke key.",
      );
    }
  };

  const onRotate = async (key) => {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `Rotate "${key.name}"? The current token will stop working immediately.`,
      )
    )
      return;
    try {
      const { data } = await api.post(`/admin/api-keys/${key.key_id}/rotate`);
      toast.success("Key rotated");
      setCreatedKey(data);
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to rotate key.",
      );
    }
  };

  const onTestWebhook = async (key) => {
    try {
      const { data } = await api.post(
        `/admin/api-keys/${key.key_id}/test-webhook`,
      );
      if (data.ok) {
        toast.success(`Webhook OK (${data.status})`);
      } else {
        toast.error(`Webhook responded ${data.status}`);
      }
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Webhook test failed.",
      );
    }
  };

  const onSaveEdit = async (key_id, patch) => {
    try {
      await api.patch(`/admin/api-keys/${key_id}`, patch);
      toast.success("Key updated");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to update key.",
      );
    }
  };

  return (
    <AdminLayout
      title="API keys"
      subtitle="Issue partner API keys so external apps and partner companies can plug into our jobs, pathways and candidate flows."
      actions={
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-800"
          data-testid="admin-api-keys-new-button"
        >
          <Plus className="h-3.5 w-3.5" /> New API key
        </button>
      }
    >
      {loading ? (
        <div
          className="flex items-center gap-2 text-slate-500 text-sm"
          data-testid="admin-api-keys-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" /> Loading API keys…
        </div>
      ) : keys.length === 0 ? (
        <EmptyState
          title="No partner API keys yet"
          body="Create your first key to let an external app or partner company access our APIs."
          testid="admin-api-keys-empty"
        />
      ) : (
        <Card className="overflow-hidden" testid="admin-api-keys-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Name / Partner</th>
                <th className="px-4 py-3 text-left">Token</th>
                <th className="px-4 py-3 text-left">Scopes</th>
                <th className="px-4 py-3 text-left">Limit</th>
                <th className="px-4 py-3 text-left">Last used</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {keys.map((k) => (
                <KeyRow
                  key={k.key_id}
                  row={k}
                  scopes={scopes}
                  onRevoke={() => onRevoke(k)}
                  onRotate={() => onRotate(k)}
                  onTestWebhook={() => onTestWebhook(k)}
                  onEdit={() => setEditing(k)}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card className="mt-6 p-5" testid="admin-api-keys-docs">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-medium text-slate-900">
              How partners use these keys
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Quick reference. Full integration guide is in our developer FAQ —
              shareable as a webpage or PDF.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/developer-faq"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              data-testid="admin-api-keys-open-faq"
            >
              Open API FAQ
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/api-faq.pdf`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
              data-testid="admin-api-keys-download-faq-pdf"
            >
              <FileText className="h-3 w-3" /> Download PDF
            </a>
          </div>
        </div>
        <p className="mt-4 text-sm text-slate-600 leading-relaxed">
          Send the token in the <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">Authorization</code> header:
        </p>
        <pre className="mt-3 rounded-lg bg-slate-950 text-slate-100 p-3 text-xs overflow-x-auto">
          <code>{`curl -H "Authorization: Bearer tamj_live_XXXX..." \\
     "${window.location.origin}/api/partner/v1/me"`}</code>
        </pre>
        <ul className="mt-4 text-sm text-slate-600 leading-relaxed list-disc pl-5 space-y-1.5">
          <li><span className="font-mono text-xs">GET /api/partner/v1/me</span> — sanity check (no scope required)</li>
          <li><span className="font-mono text-xs">GET /api/partner/v1/jobs?email=…</span> — needs <code>jobs:read</code></li>
          <li><span className="font-mono text-xs">GET /api/partner/v1/pathways</span> — needs <code>pathways:read</code></li>
          <li><span className="font-mono text-xs">POST /api/partner/v1/candidates</span> — needs <code>candidates:write</code></li>
          <li><span className="font-mono text-xs">GET /api/partner/v1/reports/{`{user_id}`}</span> — needs <code>reports:read</code> AND candidate consent</li>
          <li>Outbound webhooks are signed with HMAC-SHA256 over <code>{`{timestamp}.{body}`}</code> in <code>X-TAMJ-Signature: t=…,v1=…</code></li>
        </ul>
      </Card>

      {creating && (
        <CreateKeyDialog
          scopes={scopes}
          onCancel={() => setCreating(false)}
          onSubmit={onCreate}
        />
      )}
      {createdKey && (
        <RevealKeyDialog
          payload={createdKey}
          onClose={() => setCreatedKey(null)}
        />
      )}
      {editing && (
        <EditKeyDialog
          row={editing}
          scopes={scopes}
          onCancel={() => setEditing(null)}
          onSave={(patch) => onSaveEdit(editing.key_id, patch)}
        />
      )}
    </AdminLayout>
  );
}

function KeyRow({ row, scopes, onRevoke, onRotate, onTestWebhook, onEdit }) {
  const status = row.revoked ? "Revoked" : "Active";
  const tone = row.revoked ? "rose" : "green";
  const hasWebhook = (row.scopes || []).includes("webhook:receive");
  return (
    <tr data-testid={`admin-api-keys-row-${row.key_id}`}>
      <td className="px-4 py-3 align-top">
        <div className="font-medium text-slate-900">{row.name}</div>
        <div className="text-xs text-slate-500 mt-0.5">
          {row.partner_org || "—"}
          {row.contact_email ? ` · ${row.contact_email}` : ""}
        </div>
      </td>
      <td className="px-4 py-3 align-top">
        <code className="text-xs font-mono text-slate-700">
          {row.masked_token}
        </code>
        <div className="text-[11px] text-slate-400 mt-1">
          Created {fmtDateTime(row.created_at)}
          {row.created_by ? ` by ${row.created_by}` : ""}
        </div>
      </td>
      <td className="px-4 py-3 align-top">
        <div className="flex flex-wrap gap-1">
          {(row.scopes || []).map((s) => (
            <span
              key={s}
              className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-mono text-slate-700"
            >
              {s}
            </span>
          ))}
          {(!row.scopes || row.scopes.length === 0) && (
            <span className="text-xs text-slate-400 italic">no scopes</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 align-top text-xs text-slate-600">
        {row.rate_limit_per_minute}/min
      </td>
      <td className="px-4 py-3 align-top text-xs text-slate-600">
        {row.last_used_at ? (
          <>
            <div>{fmtDateTime(row.last_used_at)}</div>
            <div className="text-slate-400">{row.usage_count} total</div>
          </>
        ) : (
          <span className="text-slate-400">Never</span>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <StatusPill value={status} tone={tone} />
      </td>
      <td className="px-4 py-3 align-top text-right">
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            disabled={row.revoked}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            data-testid={`admin-api-keys-edit-${row.key_id}`}
          >
            Edit
          </button>
          {hasWebhook && row.webhook_url && (
            <button
              type="button"
              onClick={onTestWebhook}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
              data-testid={`admin-api-keys-test-webhook-${row.key_id}`}
            >
              <Send className="h-3 w-3" /> Test
            </button>
          )}
          <button
            type="button"
            onClick={onRotate}
            disabled={row.revoked}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            data-testid={`admin-api-keys-rotate-${row.key_id}`}
          >
            <RefreshCw className="h-3 w-3" /> Rotate
          </button>
          <button
            type="button"
            onClick={onRevoke}
            disabled={row.revoked}
            className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-40"
            data-testid={`admin-api-keys-revoke-${row.key_id}`}
          >
            <Trash2 className="h-3 w-3" /> Revoke
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------- Dialogs ----------
function Dialog({ title, onClose, children, testid }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/50 flex items-center justify-center px-4"
      data-testid={testid}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-medium text-slate-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            data-testid={`${testid}-close`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-5">{children}</div>
      </div>
    </div>
  );
}

function ScopePicker({ scopes, selected, onChange, idPrefix }) {
  const toggle = (s) =>
    onChange(
      selected.includes(s) ? selected.filter((x) => x !== s) : [...selected, s],
    );
  return (
    <div className="space-y-2" data-testid={`${idPrefix}-scopes`}>
      {scopes.map((s) => (
        <label
          key={s.key}
          className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 cursor-pointer hover:bg-slate-50"
        >
          <input
            type="checkbox"
            checked={selected.includes(s.key)}
            onChange={() => toggle(s.key)}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
            data-testid={`${idPrefix}-scope-${s.key.replace(":", "-")}`}
          />
          <div className="min-w-0">
            <div className="text-xs font-mono text-slate-900">{s.key}</div>
            <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
          </div>
        </label>
      ))}
    </div>
  );
}

function CreateKeyDialog({ scopes, onCancel, onSubmit }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    partner_org: "",
    contact_email: "",
    scopes: ["jobs:read"],
    rate_limit_per_minute: 60,
    expires_at: "",
    notes: "",
    webhook_url: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Give the key a name");
      return;
    }
    if (form.scopes.length === 0) {
      toast.error("Pick at least one scope");
      return;
    }
    setBusy(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        partner_org: form.partner_org.trim() || null,
        contact_email: form.contact_email.trim() || null,
        scopes: form.scopes,
        rate_limit_per_minute: Number(form.rate_limit_per_minute) || 60,
        expires_at: form.expires_at.trim() || null,
        notes: form.notes.trim() || null,
        webhook_url: form.webhook_url.trim() || null,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title="New partner API key" onClose={onCancel} testid="admin-api-keys-create-dialog">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name (internal label) *">
          <input
            type="text"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Acme Corp staff portal"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            data-testid="admin-api-keys-create-name"
            required
          />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Partner organisation">
            <input
              type="text"
              value={form.partner_org}
              onChange={(e) => set("partner_org", e.target.value)}
              placeholder="Acme Corp"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              data-testid="admin-api-keys-create-org"
            />
          </Field>
          <Field label="Contact email">
            <input
              type="email"
              value={form.contact_email}
              onChange={(e) => set("contact_email", e.target.value)}
              placeholder="partnerships@acme.com"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              data-testid="admin-api-keys-create-email"
            />
          </Field>
        </div>
        <Field label="Scopes *">
          <ScopePicker
            scopes={scopes}
            selected={form.scopes}
            onChange={(v) => set("scopes", v)}
            idPrefix="admin-api-keys-create"
          />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Rate limit (requests / minute)">
            <input
              type="number"
              min={0}
              max={10000}
              value={form.rate_limit_per_minute}
              onChange={(e) => set("rate_limit_per_minute", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              data-testid="admin-api-keys-create-rate"
            />
          </Field>
          <Field label="Expires at (optional, ISO datetime)">
            <input
              type="datetime-local"
              value={form.expires_at}
              onChange={(e) => set("expires_at", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              data-testid="admin-api-keys-create-expires"
            />
          </Field>
        </div>
        {form.scopes.includes("webhook:receive") && (
          <Field label="Webhook URL (partner endpoint we POST events to)">
            <input
              type="url"
              value={form.webhook_url}
              onChange={(e) => set("webhook_url", e.target.value)}
              placeholder="https://partner.example.com/webhooks/tamj"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              data-testid="admin-api-keys-create-webhook"
            />
          </Field>
        )}
        <Field label="Notes (internal)">
          <textarea
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            rows={3}
            placeholder="What's the partnership about? Who's the technical contact?"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            data-testid="admin-api-keys-create-notes"
          />
        </Field>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid="admin-api-keys-create-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="admin-api-keys-create-submit"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
            Create key
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function EditKeyDialog({ row, scopes, onCancel, onSave }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: row.name || "",
    partner_org: row.partner_org || "",
    contact_email: row.contact_email || "",
    scopes: row.scopes || [],
    rate_limit_per_minute: row.rate_limit_per_minute ?? 60,
    expires_at: row.expires_at || "",
    notes: row.notes || "",
    webhook_url: row.webhook_url || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({
        name: form.name.trim(),
        partner_org: form.partner_org.trim(),
        contact_email: form.contact_email.trim() || null,
        scopes: form.scopes,
        rate_limit_per_minute: Number(form.rate_limit_per_minute) || 60,
        expires_at: form.expires_at.trim() || "",
        notes: form.notes.trim(),
        webhook_url: form.webhook_url.trim(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={`Edit "${row.name}"`} onClose={onCancel} testid="admin-api-keys-edit-dialog">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name">
          <input
            type="text"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            data-testid="admin-api-keys-edit-name"
          />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Partner organisation">
            <input
              type="text"
              value={form.partner_org}
              onChange={(e) => set("partner_org", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Contact email">
            <input
              type="email"
              value={form.contact_email}
              onChange={(e) => set("contact_email", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </Field>
        </div>
        <Field label="Scopes">
          <ScopePicker
            scopes={scopes}
            selected={form.scopes}
            onChange={(v) => set("scopes", v)}
            idPrefix="admin-api-keys-edit"
          />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Rate limit (per minute)">
            <input
              type="number"
              min={0}
              max={10000}
              value={form.rate_limit_per_minute}
              onChange={(e) => set("rate_limit_per_minute", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Expires at (ISO datetime)">
            <input
              type="datetime-local"
              value={form.expires_at}
              onChange={(e) => set("expires_at", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </Field>
        </div>
        {form.scopes.includes("webhook:receive") && (
          <Field label="Webhook URL">
            <input
              type="url"
              value={form.webhook_url}
              onChange={(e) => set("webhook_url", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
              placeholder="https://partner.example.com/webhooks/tamj"
            />
          </Field>
        )}
        <Field label="Notes">
          <textarea
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </Field>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            data-testid="admin-api-keys-edit-save"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            Save changes
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function RevealKeyDialog({ payload, onClose }) {
  const tokenRef = useRef(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  return (
    <Dialog
      title="Save this token now"
      onClose={acknowledged ? onClose : () => {}}
      testid="admin-api-keys-reveal-dialog"
    >
      <div className="space-y-5">
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900 leading-relaxed">
            <p className="font-medium">This is the only time you'll see the full token.</p>
            <p className="mt-1">
              We only store a hash. If you lose it, rotate the key to generate a new
              one. Treat this like a password — never commit it to source control.
            </p>
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-slate-700 mb-1.5">API token</div>
          <div className="flex items-stretch gap-2">
            <input
              ref={tokenRef}
              readOnly
              value={payload.plaintext_token || ""}
              className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-mono text-slate-900"
              data-testid="admin-api-keys-reveal-token"
              onClick={(e) => e.target.select()}
            />
            <button
              type="button"
              onClick={() => copy(payload.plaintext_token, "Token")}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-800"
              data-testid="admin-api-keys-reveal-copy"
            >
              <Copy className="h-3 w-3" /> Copy
            </button>
          </div>
        </div>

        {payload.plaintext_webhook_secret && (
          <div>
            <div className="text-xs font-medium text-slate-700 mb-1.5">
              Webhook signing secret
            </div>
            <div className="flex items-stretch gap-2">
              <input
                readOnly
                value={payload.plaintext_webhook_secret || ""}
                className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-mono text-slate-900"
                data-testid="admin-api-keys-reveal-webhook-secret"
                onClick={(e) => e.target.select()}
              />
              <button
                type="button"
                onClick={() =>
                  copy(payload.plaintext_webhook_secret, "Webhook secret")
                }
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-800"
              >
                <Copy className="h-3 w-3" /> Copy
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              We sign every webhook payload with this secret using HMAC-SHA256.
              Verify the <code>X-TAMJ-Signature</code> header on your endpoint.
            </p>
          </div>
        )}

        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300"
            data-testid="admin-api-keys-reveal-ack"
          />
          I've saved the token in a secure place.
        </label>

        <div className="flex items-center justify-end pt-2 border-t border-slate-100">
          <button
            type="button"
            disabled={!acknowledged}
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="admin-api-keys-reveal-done"
          >
            <ShieldCheck className="h-3 w-3" /> Done
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
